import {
  AppBar as MuiAppBar,
  AppBarProps as MuiAppBarProps,
  Stack,
  Toolbar,
  Typography,
  styled,
  useTheme
} from "@mui/material";
import React from "react";

import { EnhancedCustomer } from "../../Models/Customer";
import { Lead } from "../../Models/Lead";
import Clock from "./Items/Clock";
import IvrConnection from "./Items/IvrConnection";
import LiveNewLeadRate from "./Items/LiveNewLeadRate";
import Logout from "./Items/Logout";
import Notifications from "./Items/Notifications";
import UserAccountMenu from "./Items/UserAccountMenu";
import Version from "./Items/Version/Version";

interface Props {
  businessUnit?: string;
  customer?: EnhancedCustomer;
  drawerCollapsedSize?: number;
  drawerOpenSize?: number;
  errors?: unknown[];
  isDrawerOpen?: boolean;
  position?: AppBarProps["position"];
  session?: { lead: Lead; complete: boolean };
  focusIvrContact?: (ivrContactId: string) => void;
  updateLeadIvrContactId?: (phoneNumber: string, ivrContactId: number) => void;
}

const Navigation: React.FC<Props> = ({
  businessUnit,
  customer,
  drawerCollapsedSize = 0,
  drawerOpenSize = 0,
  isDrawerOpen = false,
  position,
  session,
  focusIvrContact,
  updateLeadIvrContactId
}: Props) => {
  const theme = useTheme();
  return (
    <AppBar
      position={position || "static"}
      open={isDrawerOpen}
      drawerOpenWidth={drawerOpenSize}
      drawerClosedWidth={drawerCollapsedSize}
    >
      <StyledToolbar>
        <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%" px={2}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <svg
              style={{ width: "5rem", fill: theme.palette.primary.contrastText }}
              viewBox="0 0 200.000000 200.000000"
              preserveAspectRatio="xMidYMid meet"
            >
              <g transform="translate(0.000000,200.000000) scale(0.100000,-0.100000)" stroke="none">
                <path
                  d="M623 1746 c-166 -28 -303 -53 -306 -56 -2 -3 -75 -99 -161 -215 -112
            -149 -156 -216 -153 -231 4 -24 904 -1030 930 -1039 9 -3 25 -3 35 0 9 4 222
            234 472 513 328 365 455 513 455 530 0 26 -298 436 -324 447 -19 8 -609 105
            -630 104 -9 -1 -152 -24 -318 -53z m52 -196 l-119 -119 -60 89 c-32 50 -57 92
            -55 93 5 4 307 54 334 56 13 1 -18 -37 -100 -119z m415 -15 l135 -135 -275 0
            -275 0 135 135 c74 74 137 135 140 135 3 0 66 -61 140 -135z m203 108 c92 -15
            167 -30 167 -33 0 -3 -26 -44 -58 -92 l-58 -87 -119 119 c-66 66 -115 120
            -110 120 6 0 85 -12 178 -27z m-883 -173 c29 -44 51 -82 49 -84 -8 -7 -271
            -58 -276 -53 -6 7 159 227 167 222 4 -3 31 -41 60 -85z m1234 -28 c43 -57 76
            -106 73 -109 -5 -5 -268 45 -276 53 -2 2 22 43 53 90 32 48 61 82 65 78 5 -5
            43 -55 85 -112z m-383 -189 c-10 -27 -83 -227 -162 -445 -79 -219 -146 -398
            -149 -398 -3 0 -70 179 -149 398 -79 218 -152 418 -162 445 l-19 47 330 0 330
            0 -19 -47z m-733 10 c5 -16 65 -179 132 -363 67 -184 129 -353 136 -375 l15
            -40 -35 35 c-66 67 -626 693 -623 697 5 4 326 71 349 72 9 1 20 -11 26 -26z
            m1042 -8 c95 -19 175 -36 177 -38 3 -4 -558 -631 -623 -697 l-35 -35 15 40 c7
            22 69 191 136 375 67 184 127 347 132 363 6 15 13 27 17 27 4 0 86 -16 181
            -35z"
                />
              </g>
            </svg>
            <Typography variant="h5">{`GEM${businessUnit ? ` - ${businessUnit}` : ""}`}</Typography>
            <Version />
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <IvrConnection focusIvrContact={focusIvrContact} updateLeadIvrContactId={updateLeadIvrContactId} />
            <Clock center={!session?.complete ? customer?.center : undefined} />
            <Notifications />
            <UserAccountMenu />
            <LiveNewLeadRate />
            <Logout />
          </Stack>
        </Stack>
      </StyledToolbar>
    </AppBar>
  );
};

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
  drawerOpenWidth: number;
  drawerClosedWidth: number;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: prop => prop !== "open" && prop !== "drawerOpenWidth" && prop !== "drawerClosedWidth"
})<AppBarProps>(({ theme, open, drawerOpenWidth, drawerClosedWidth }) => ({
  zIndex: theme.zIndex.drawer + 1,
  marginLeft: drawerClosedWidth,
  width: `calc(100% - ${drawerClosedWidth}px)`,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  ...(open && {
    marginLeft: drawerOpenWidth,
    width: `calc(100% - ${drawerOpenWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  })
}));

const StyledToolbar = styled(Toolbar)(() => ({
  padding: "unset !important",
  "@media all": { minHeight: 80 }
}));

export default Navigation;
